<template>
  <SettingsCard title="Dados pessoais" description="Suas informações de identificação e contato.">
    <template #action>
      <OutlineButton
        v-if="!isEditing"
        text="Editar"
        width="auto"
        data-testid="personal-data-edit-button"
        @click="startEditing"
      />
    </template>

    <!-- Modo visualização -->
    <div v-if="!isEditing" class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
      <ReadonlyField :icon="mdiAccountOutline" label="Nome" :value="userData?.name" />
      <ReadonlyField :icon="mdiEmailOutline" label="E-mail" :value="userData?.email" />
      <ReadonlyField
        :icon="mdiCardAccountDetailsOutline"
        label="CPF"
        :value="maskCPF(userData?.cpf)"
      />
      <ReadonlyField
        :icon="mdiCakeVariantOutline"
        label="Data de nascimento"
        :value="isoToBrDate(userData?.birthDate)"
      />
      <ReadonlyField
        :icon="mdiPhoneOutline"
        label="Telefone"
        :value="formatPhone(userData?.phoneNumber)"
      />
      <ReadonlyField
        :icon="mdiCalendarCheckOutline"
        label="Conta criada em"
        :value="isoToBrDate(userData?.createdAt)"
      />
    </div>

    <!-- Modo edição -->
    <form v-else class="flex flex-col gap-4" @submit.prevent="handleSubmit">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
        <GenericInput
          v-model="form.name"
          label="Nome"
          required
          data-testid="personal-data-name-input"
          :error-messages="errors.name"
        />
        <GenericInput
          v-model="form.email"
          label="E-mail"
          type="email"
          required
          data-testid="personal-data-email-input"
          :error-messages="errors.email"
        />
        <GenericInput
          v-model="form.cpf"
          label="CPF"
          mask="###.###.###-##"
          data-testid="personal-data-cpf-input"
          :error-messages="errors.cpf"
        />
        <GenericInput
          v-model="form.birthDate"
          label="Data de nascimento"
          mask="##/##/####"
          data-testid="personal-data-birthdate-input"
          :error-messages="errors.birthDate"
        />
        <GenericInput
          v-model="form.phoneNumber"
          label="Telefone"
          mask="(##) #####-####"
          data-testid="personal-data-phone-input"
          :error-messages="errors.phoneNumber"
        />
      </div>

      <div class="flex justify-end gap-3 pt-2">
        <OutlineButton
          text="Cancelar"
          width="140px"
          type="button"
          data-testid="personal-data-cancel-button"
          @click="cancelEditing"
        />
        <FilledButton
          text="Salvar alterações"
          width="180px"
          type="submit"
          :loading="saving"
          data-testid="personal-data-save-button"
        />
      </div>
    </form>
  </SettingsCard>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from 'vue'
import { ValidationError } from 'yup'
import {
  mdiAccountOutline,
  mdiEmailOutline,
  mdiCardAccountDetailsOutline,
  mdiCakeVariantOutline,
  mdiPhoneOutline,
  mdiCalendarCheckOutline
} from '@mdi/js'

import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import type { UserDataResponse } from '@/Network/Types/Responses/UserResponse'

import SettingsCard from '../atoms/SettingsCard.vue'
import ReadonlyField from '../atoms/ReadonlyField.vue'
import { personalDataSchema } from '../../schemas'
import {
  formatCPF,
  maskCPF,
  formatPhone,
  isoToBrDate,
  brDateToIso,
  onlyDigits
} from '../../helpers/profile'

const props = defineProps<{
  userData?: UserDataResponse | null
  // Handler assíncrono: deve resolver em sucesso e rejeitar em erro (o pai exibe o toast).
  save: (payload: Partial<UserDataResponse>) => Promise<void>
}>()

const isEditing = ref(false)
const saving = ref(false)
const errors = reactive<Record<string, string>>({})

type PersonalForm = {
  name: string
  email: string
  cpf: string
  birthDate: string
  phoneNumber: string
}

const form = reactive<PersonalForm>({
  name: '',
  email: '',
  cpf: '',
  birthDate: '',
  phoneNumber: ''
})

const clearErrors = () => Object.keys(errors).forEach((key) => delete errors[key])

const populateForm = () => {
  const data = props.userData
  form.name = data?.name ?? ''
  form.email = data?.email ?? ''
  form.cpf = formatCPF(data?.cpf)
  form.birthDate = isoToBrDate(data?.birthDate)
  form.phoneNumber = formatPhone(data?.phoneNumber)
}

// Mantém o modo visualização em sincronia quando os dados chegam do backend
// (evita sobrescrever o que o usuário está digitando durante a edição).
watch(
  () => props.userData,
  () => {
    if (!isEditing.value) populateForm()
  },
  { immediate: true }
)

const startEditing = () => {
  populateForm()
  clearErrors()
  isEditing.value = true
}

const cancelEditing = () => {
  populateForm()
  clearErrors()
  isEditing.value = false
}

const handleSubmit = async () => {
  clearErrors()
  try {
    await personalDataSchema.validate(form, { abortEarly: false })
  } catch (err) {
    if (err instanceof ValidationError) {
      const inner = err.inner?.length ? err.inner : [err]
      inner.forEach((e) => {
        if (e.path) errors[e.path] = e.message
      })
    }
    return
  }

  // backend rejeita phoneNumber vazio (400) — omitir o campo quando não preenchido
  const phoneDigits = onlyDigits(form.phoneNumber)
  const payload: Partial<UserDataResponse> = {
    name: form.name.trim(),
    email: form.email.trim(),
    cpf: onlyDigits(form.cpf) || null,
    birthDate: brDateToIso(form.birthDate),
    ...(phoneDigits ? { phoneNumber: phoneDigits } : {})
  }

  saving.value = true
  try {
    await props.save(payload)
    isEditing.value = false // fecha só em sucesso real deste formulário
  } catch {
    // o pai já exibiu o toast de erro; mantém o modo de edição para não perder o que foi digitado
  } finally {
    saving.value = false
  }
}
</script>
